"use client";

import { useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { ArrowLeft, Loader2, ShieldPlus } from "lucide-react";
import { AuthMethodPicker } from "@/components/auth/auth-method-picker";
import { VerificationCodeInput } from "@/components/auth/verification-code-input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { useLogin } from "@/hooks/use-auth";
import { useSocialAuth } from "@/hooks/use-social-auth";
import { useMagicLinkPolling } from "@/hooks/use-magic-link-polling";
import type { LoginRequest } from "@/lib/api/auth";
import {
  normalizePhone,
  validatePhone,
  type PhoneValidationError,
} from "@/lib/phone";

interface EmailErrors {
  email?: string;
  password?: string;
}

interface PhoneErrors {
  phone?: PhoneValidationError;
}

type Step = "choose" | "email" | "phone" | "verification";
type VerificationMethod = "email" | "phone";
type EmailAction = "password" | "code" | null;

function validateEmail(
  email: string,
  { requirePassword, password }: { requirePassword: boolean; password?: string },
): EmailErrors {
  const errors: EmailErrors = {};
  if (!email) errors.email = "emailRequired";
  if (requirePassword && !password) errors.password = "passwordRequired";
  return errors;
}

export default function LoginPage() {
  const t = useTranslations("auth");
  const tc = useTranslations("common");
  const searchParams = useSearchParams();
  const magicLinkError = searchParams.get("error");

  const [step, setStep] = useState<Step>("choose");
  const [verificationMethod, setVerificationMethod] = useState<VerificationMethod | null>(null);
  const [verificationRequest, setVerificationRequest] = useState<LoginRequest | null>(null);
  const [emailAction, setEmailAction] = useState<EmailAction>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailErrors, setEmailErrors] = useState<EmailErrors>({});

  const [phone, setPhone] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [phoneErrors, setPhoneErrors] = useState<PhoneErrors>({});

  const emailLogin = useLogin();
  const phoneLogin = useLogin();
  const social = useSocialAuth();

  // Detect cross-tab magic link completion
  useMagicLinkPolling(step === "verification" && verificationMethod === "email");

  const socialLoadingMethod = social.isGoogleLoading
    ? ("google" as const)
    : social.isFacebookLoading
      ? ("facebook" as const)
      : null;

  const verificationMutation = verificationMethod === "phone" ? phoneLogin : emailLogin;
  const verificationError = verificationMutation.error?.requiresVerification
    ? null
    : verificationMutation.error;

  function clearVerificationState() {
    setVerificationMethod(null);
    setVerificationRequest(null);
  }

  function resetLoginState() {
    setEmailAction(null);
    clearVerificationState();
    setEmailErrors({});
    setPhoneErrors({});
    emailLogin.reset();
    phoneLogin.reset();
  }

  function handleGoogleCredential(credential: string) {
    social.handleGoogleToken(credential);
  }

  function handleMethodSelect(method: "facebook" | "email" | "phone") {
    if (method === "facebook") {
      social.loginWithFacebook();
      return;
    }

    resetLoginState();
    setStep(method);
  }

  function backToChoose() {
    resetLoginState();
    setStep("choose");
  }

  function startVerificationFlow(method: VerificationMethod, request: LoginRequest) {
    setVerificationMethod(method);
    setVerificationRequest(request);
    setEmailAction(null);
    if (method === "email") {
      emailLogin.reset();
    } else {
      phoneLogin.reset();
    }
    setStep("verification");
  }

  function backFromVerification() {
    if (!verificationMethod) {
      backToChoose();
      return;
    }

    clearVerificationState();
    emailLogin.reset();
    phoneLogin.reset();
    setStep(verificationMethod);
  }

  function doEmailLogin() {
    const normalizedEmail = email.trim();
    const errors = validateEmail(normalizedEmail, {
      requirePassword: true,
      password,
    });
    setEmailErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const request: LoginRequest = {
      authMethod: "email_auth",
      credential: normalizedEmail,
      credentialType: "email",
      password,
    };

    setEmailAction("password");
    emailLogin.mutate(request, {
      onError: (error) => {
        if (error.requiresVerification) {
          startVerificationFlow("email", {
            authMethod: "email_auth",
            credential: normalizedEmail,
            credentialType: "email",
          });
        }
      },
    });
  }

  function doEmailCodeLogin() {
    const normalizedEmail = email.trim();
    const errors = validateEmail(normalizedEmail, { requirePassword: false });
    setEmailErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const request: LoginRequest = {
      authMethod: "email_auth",
      credential: normalizedEmail,
      credentialType: "email",
    };

    setEmailAction("code");
    emailLogin.mutate(request, {
      onError: (error) => {
        if (error.requiresVerification) {
          startVerificationFlow("email", request);
        }
      },
    });
  }

  function doPhoneLogin() {
    const phoneError = validatePhone(phone);
    setPhoneErrors(phoneError ? { phone: phoneError } : {});
    if (phoneError) return;

    const request: LoginRequest = {
      authMethod: "phone_auth",
      credential: normalizePhone(phone),
      credentialType: "phone",
    };

    phoneLogin.mutate(request, {
      onError: (error) => {
        if (error.requiresVerification) {
          startVerificationFlow("phone", request);
        }
      },
    });
  }

  function doVerificationLogin(code: string) {
    if (!verificationMethod || !verificationRequest) return;

    const request: LoginRequest = verificationMethod === "email"
      ? {
          authMethod: "email_auth",
          credential: verificationRequest.credential,
          credentialType: "email",
          verificationCode: code,
        }
      : {
          ...verificationRequest,
          verificationCode: code,
        };

    const mutation = verificationMethod === "email" ? emailLogin : phoneLogin;
    mutation.mutate(request);
  }

  const brand = (
    <Link href="/app" className="mb-8 flex flex-col items-center gap-2">
      <ShieldPlus className="h-10 w-10 text-primary" />
      <h1 className="text-xl font-semibold tracking-tight">{tc("appName")}</h1>
      {step === "choose" && (
        <p className="text-sm text-muted-foreground">{tc("tagline")}</p>
      )}
    </Link>
  );

  if (step === "email") {
    return (
      <>
        {brand}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {t("signInWithEmail")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                doEmailLogin();
              }}
              className="space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base">{t("email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 text-base"
                  aria-invalid={!!emailErrors.email}
                />
                {emailErrors.email && (
                  <p className="text-sm text-destructive">{t(emailErrors.email)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-base">{t("password")}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 text-base"
                  aria-invalid={!!emailErrors.password}
                />
                {emailErrors.password && (
                  <p className="text-sm text-destructive">{t(emailErrors.password)}</p>
                )}
                <Link
                  href="/forgot-password"
                  className="inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  {t("forgotPassword")}
                </Link>
              </div>

              {emailLogin.error && !emailLogin.error.requiresVerification && (
                <p className="text-sm text-destructive">
                  {emailLogin.error.status === 0
                    ? tc("networkError")
                    : emailLogin.error.message || t("invalidCredentials")}
                </p>
              )}

              <Button
                className="h-12 w-full whitespace-normal text-base"
                disabled={emailLogin.isPending}
              >
                {emailLogin.isPending && emailAction === "password"
                  ? t("signingIn")
                  : t("signIn")}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="h-12 w-full whitespace-normal text-base"
                onClick={doEmailCodeLogin}
                disabled={emailLogin.isPending}
              >
                {emailLogin.isPending && emailAction === "code"
                  ? t("sendingCode")
                  : t("sendCode")}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="h-12 w-full gap-2 text-base"
                onClick={backToChoose}
                disabled={emailLogin.isPending}
              >
                <ArrowLeft className="size-5" />
                {t("otherMethods")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </>
    );
  }

  if (step === "phone") {
    return (
      <>
        {brand}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {t("signInWithPhone")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                doPhoneLogin();
              }}
              className="space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-base">{t("phone")}</Label>
                <PhoneInput
                  id="phone"
                  value={phone}
                  onChange={(value, meta) => {
                    setPhone(value);
                    setPhoneDisplay(meta.inputValue);
                  }}
                  size="default"
                  aria-invalid={!!phoneErrors.phone}
                />
                {phoneErrors.phone && (
                  <p className="text-sm text-destructive">{t(phoneErrors.phone)}</p>
                )}
              </div>

              {phoneLogin.error && !phoneLogin.error.requiresVerification && (
                <p className="text-sm text-destructive">
                  {phoneLogin.error.status === 0
                    ? tc("networkError")
                    : phoneLogin.error.message || t("invalidCredentials")}
                </p>
              )}

              <Button
                className="h-12 w-full whitespace-normal text-base"
                disabled={phoneLogin.isPending}
              >
                {phoneLogin.isPending ? (
                  <>
                    <Loader2 className="size-5 animate-spin" />
                    {t("sendingCode")}
                  </>
                ) : (
                  t("sendCode")
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="h-12 w-full gap-2 text-base"
                onClick={backToChoose}
                disabled={phoneLogin.isPending}
              >
                <ArrowLeft className="size-5" />
                {t("otherMethods")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </>
    );
  }

  if (step === "verification" && verificationMethod && verificationRequest) {
    const description = verificationMethod === "email"
      ? t("verifyDescription", { email: verificationRequest.credential })
      : t("otpSentTo", { phone: phoneDisplay || phone });

    return (
      <>
        {brand}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {verificationMethod === "email" ? t("verifyTitle") : t("verifyPhoneTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VerificationCodeInput
              description={description}
              isPending={verificationMutation.isPending}
              error={verificationError}
              onSubmit={doVerificationLogin}
              onBack={backFromVerification}
            />
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      {brand}
      {magicLinkError && (
        <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {magicLinkError === "invalid_or_expired_token" || magicLinkError === "session_expired"
            ? t("magicLinkExpired")
            : t("magicLinkError")}
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {t("welcomeBack")}
          </CardTitle>
          <CardDescription className="text-base">
            {t("chooseMethodDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <AuthMethodPicker
            mode="login"
            onSelect={handleMethodSelect}
            onGoogleCredential={handleGoogleCredential}
            loadingMethod={socialLoadingMethod}
            isFacebookReady={social.isFacebookReady}
            error={social.error}
          />

          <p className="text-center text-sm text-muted-foreground">
            {t("noAccount")}{" "}
            <Link
              href="/register"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              {t("createOne")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </>
  );
}
